/**
 * Quick Start Dialog - Onboarding für neue Admin-Benutzer
 * 
 * Erscheint automatisch beim ersten Admin-Login und führt
 * durch den Happy Path Workflow.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import {
  Sparkles,
  Database,
  Network,
  Layers,
  FileText,
  ArrowRight,
  Rocket,
  CheckCircle,
  Clock
} from 'lucide-react';

const QUICK_START_STORAGE_KEY = 'exam-trainer-quick-start-dismissed';

const QuickStartDialog = ({ 
  isOpen = false, 
  onClose,
  onStartGuide,
  userName = 'Admin'
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  
  const steps = [
    {
      icon: Database,
      title: 'Sources & Evidence',
      description: 'Laden Sie Ihre Quellen hoch (PDFs, Texte) und lassen Sie diese in Chunks aufteilen.',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: Network,
      title: 'Ontology Proposals',
      description: 'Extrahieren Sie Konzepte und Begriffe automatisch aus Ihren Quellen.',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      icon: Layers,
      title: 'AKUs (Claims)',
      description: 'Erstellen Sie prüfungsrelevante Wissenseinheiten aus Ihren Inhalten.',
      color: 'bg-yellow-100 text-yellow-600'
    },
    {
      icon: FileText,
      title: 'Question Racks',
      description: 'Bauen Sie strukturierte Frage-Vorlagen aus verwandten AKUs.',
      color: 'bg-indigo-100 text-indigo-600'
    },
    {
      icon: Sparkles,
      title: 'Items generieren',
      description: 'Generieren Sie hochwertige Prüfungsfragen automatisch.',
      color: 'bg-orange-100 text-orange-600'
    }
  ];

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem(QUICK_START_STORAGE_KEY, 'true');
    }
    onClose?.();
  };

  const handleStartGuide = () => {
    if (dontShowAgain) {
      localStorage.setItem(QUICK_START_STORAGE_KEY, 'true');
    }
    onStartGuide?.();
  };

  // Check if should show
  const shouldShow = () => {
    return localStorage.getItem(QUICK_START_STORAGE_KEY) !== 'true';
  };

  return (
    <Dialog open={isOpen && shouldShow()} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] p-0 overflow-hidden flex flex-col" data-testid="quick-start-dialog">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-orange-500 p-6 text-white flex-shrink-0">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-white/20 rounded-lg">
              <Rocket className="w-6 h-6" />
            </div>
            <DialogTitle className="text-2xl font-bold text-white">
              Willkommen, {userName}!
            </DialogTitle>
          </div>
          <DialogDescription className="text-white/90 text-base">
            Ihr Exam Quality Engine ist bereit. In nur 5 Schritten erstellen Sie Ihre erste Prüfungsfrage.
          </DialogDescription>
        </div>

        {/* Steps overview - scrollable */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-500">Geschätzte Zeit: 5-10 Minuten</span>
          </div>

          {/* Step cards */}
          <div className="space-y-3">
            {steps.map((step, index) => (
              <div 
                key={index}
                className={`flex items-center gap-4 p-3 rounded-lg border transition-all duration-300 ${
                  currentStep === index ? 'border-blue-300 bg-blue-50 shadow-sm' : 'border-gray-100 hover:border-gray-200'
                }`}
                onMouseEnter={() => setCurrentStep(index)}
              >
                <div className={`p-2 rounded-lg ${step.color}`}>
                  <step.icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{index + 1}. {step.title}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <ArrowRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                )}
                {index === steps.length - 1 && (
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                )}
              </div>
            ))}
          </div>

          {/* Info badge */}
          <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
            <Sparkles className="w-5 h-5 text-green-600" />
            <p className="text-sm text-green-800">
              <strong>Tipp:</strong> Der Guided Mode führt Sie Schritt für Schritt durch den Prozess und zeigt Ihren Fortschritt an.
            </p>
          </div>
        </div>

        {/* Footer - always visible */}
        <DialogFooter className="p-6 pt-4 flex-col sm:flex-row gap-3 border-t flex-shrink-0 bg-white">
          <div className="flex items-center gap-2 mr-auto">
            <Checkbox 
              id="dontShowAgain" 
              checked={dontShowAgain}
              onCheckedChange={setDontShowAgain}
              data-testid="quick-start-dont-show"
            />
            <label 
              htmlFor="dontShowAgain" 
              className="text-sm text-gray-500 cursor-pointer"
            >
              Nicht mehr anzeigen
            </label>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleClose}
              data-testid="quick-start-skip"
            >
              Später
            </Button>
            <Button 
              onClick={handleStartGuide}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              data-testid="quick-start-begin"
            >
              <Rocket className="w-4 h-4 mr-2" />
              Los geht's!
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Helper to check if quick start should be shown
export const shouldShowQuickStart = () => {
  return localStorage.getItem(QUICK_START_STORAGE_KEY) !== 'true';
};

// Helper to reset quick start (for testing)
export const resetQuickStart = () => {
  localStorage.removeItem(QUICK_START_STORAGE_KEY);
};

export default QuickStartDialog;
